<template>
	<div class="task-group-stack">
		<section v-for="group in groups" :key="group.id" class="task-group">
			<header class="task-group-header">
				<span class="group-dot" :style="{ backgroundColor: group.color }"></span>
				<h2>{{ group.label }}</h2>
				<span class="group-count">{{ group.rows.length }}</span>
			</header>
			<div class="task-table-shell" :style="{ borderLeftColor: group.color }">
				<div class="task-table-scroll">
					<table>
						<thead>
							<tr>
								<th>{{ $t('projects.deliverable') }}</th>
								<th class="assignee-column">{{ $t('projects.assigned-to') }}</th>
								<th class="date-column">{{ $t('projects.due') }}</th>
								<th class="progress-column">{{ $t('projects.tasks') }}</th>
								<th class="action-column"></th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="row in group.rows"
								:key="row.deliverable.id"
								:class="{ 'is-subtask': row.isSubtask }"
								tabindex="0"
								@click="$emit('open-deliverable', row.deliverable)"
								@keyup.enter="$emit('open-deliverable', row.deliverable)"
							>
								<td>
									<div class="task-name" :class="{ indented: row.isSubtask }">
										<portal-icon
											:name="row.isSubtask ? 'arrow-right' : 'clipboard-list'"
											:size="15"
										></portal-icon>
										<strong>{{ row.deliverable.name }}</strong>
									</div>
								</td>
								<td>
									<div class="assignees" v-if="row.deliverable.assignedToList && row.deliverable.assignedToList.length">
										<assigned-user
											v-for="(assignedTo, index) in row.deliverable.assignedToList"
											:key="assignedTo"
											:assigned-to="assignedTo"
											:assignable-users="assignableUsers"
											:show-name="false"
											:style="{ zIndex: row.deliverable.assignedToList.length - index }"
										></assigned-user>
									</div>
									<span v-else class="empty-value">—</span>
								</td>
								<td>
									<span v-if="row.deliverable.dueDate" class="due-date">
										<portal-icon name="calendar-days" :size="14"></portal-icon>
										{{ formatDate(row.deliverable.dueDate) }}
									</span>
									<span v-else class="empty-value">—</span>
								</td>
								<td>
									<span v-if="row.deliverable.tasks && row.deliverable.tasks.length" class="checklist">
										<portal-icon name="list-checks" :size="14"></portal-icon>
										{{ completedTasks(row.deliverable) }}/{{ row.deliverable.tasks.length }}
									</span>
									<span v-else class="empty-value">—</span>
								</td>
								<td class="action-column">
									<portal-icon name="chevron-right" :size="17"></portal-icon>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
	import { DateTime } from 'luxon';
	import AssignedUser from '@/components/AssignedUser';

	export default {
		name: 'ProjectTaskTable',

		components: {
			AssignedUser,
		},

		props: {
			deliverables: { type: Array, required: true },
			statusList: { type: Array, required: true },
			assignableUsers: { type: Array, required: false, default: () => [] },
		},

		computed: {
			groups() {
				const knownStatusIds = new Set(this.statusList.map((status) => status.id));
				const groups = this.statusList.map((status) => ({
					id: status.id,
					label: status.label,
					color: status.hexColor || 'var(--portal-muted)',
					rows: this.rowsForStatus(status.id),
				}));
				const unknown = this.deliverables.filter(
					(deliverable) => !knownStatusIds.has(deliverable.statusId)
				);
				if (unknown.length) {
					groups.unshift({
						id: '__none__',
						label: this.$t('projects.no-status'),
						color: 'var(--portal-muted)',
						rows: this.flattenRows(unknown),
					});
				}
				return groups.filter((group) => group.rows.length);
			},
		},

		methods: {
			rowsForStatus(statusId) {
				return this.flattenRows(
					this.deliverables.filter((deliverable) => deliverable.statusId === statusId)
				);
			},

			flattenRows(deliverables) {
				const rows = [];
				deliverables.forEach((deliverable) => {
					rows.push({ deliverable, isSubtask: false });
					(deliverable.subtasks || []).forEach((subtask) => {
						rows.push({ deliverable: subtask, isSubtask: true });
					});
				});
				return rows;
			},

			formatDate(date) {
				return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);
			},

			completedTasks(deliverable) {
				return deliverable.tasks.filter((task) => task.complete).length;
			},
		},
	};
</script>

<style scoped lang="scss">
	.task-group-stack {
		display: grid;
		gap: 18px;
	}

	.task-group-header {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 0 3px 8px;
	}

	.group-dot {
		width: 9px;
		height: 9px;
		flex: 0 0 9px;
		border-radius: 999px;
	}

	.task-group-header h2 {
		margin: 0;
		color: var(--portal-text);
		font-size: 14px;
		font-weight: 750;
	}

	.group-count {
		padding: 1px 7px;
		border-radius: 999px;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 700;
	}

	.task-table-shell {
		border: 1px solid var(--portal-border);
		border-left-width: 3px;
		border-radius: var(--brand-surface-radius, 13px);
		background: var(--portal-table-row-background, var(--portal-surface));
		overflow: hidden;
	}

	.task-table-scroll {
		overflow-x: auto;
	}

	table {
		width: 100%;
		min-width: 720px;
		border-collapse: collapse;
		table-layout: fixed;
	}

	th {
		height: 34px;
		padding: 0 13px;
		border-bottom: 1px solid var(--portal-border);
		background: var(--brand-surface, var(--v-gray_20-base));
		color: var(--portal-muted);
		font-size: 10px;
		font-weight: 750;
		letter-spacing: 0.075em;
		text-align: left;
		text-transform: uppercase;
	}

	td {
		height: 48px;
		padding: 0 13px;
		border-bottom: 1px solid var(--v-gray_20-base);
		color: var(--portal-table-row-text, var(--portal-text));
		font-size: 13px;
	}

	tbody tr {
		cursor: pointer;
	}

	tbody tr:last-child td {
		border-bottom: 0;
	}

	tbody tr:hover td {
		background: color-mix(in srgb, var(--v-primary-base) 4%, var(--portal-table-row-background, var(--portal-surface)));
	}

	tbody tr:focus-within td {
		background: color-mix(in srgb, var(--v-primary-base) 7%, var(--portal-table-row-background, var(--portal-surface)));
	}

	.is-subtask td {
		background: var(--portal-table-row-hover, color-mix(in srgb, var(--v-gray_20-base) 55%, var(--portal-surface)));
	}

	.task-name,
	.due-date,
	.checklist,
	.assignees {
		display: flex;
		align-items: center;
		gap: 7px;
	}

	.task-name {
		min-width: 0;
	}

	.task-name.indented {
		padding-left: 24px;
		color: var(--portal-table-row-muted, var(--portal-muted));
	}

	.task-name strong {
		overflow: hidden;
		font-weight: 650;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.assignees > * + * {
		margin-left: -8px;
	}

	.due-date,
	.checklist,
	.empty-value {
		color: var(--portal-table-row-muted, var(--portal-muted));
	}

	.assignee-column {
		width: 120px;
	}

	.date-column {
		width: 145px;
	}

	.progress-column {
		width: 100px;
	}

	.action-column {
		width: 42px;
		padding-left: 4px;
		padding-right: 8px;
		color: var(--portal-muted);
		text-align: right;
	}
</style>
